/**
 * Single source of truth for the Preview launch phase-gate constants
 * (Issue #466, Codex round-2 follow-up #469, drift-pin #472).
 *
 * The gate is enforced in two places — the renderer's
 * `PreviewLaunchButton.tsx` (UX-first; instant feedback, no flicker)
 * and the main-process `preview-phase-preflight.ts` (authoritative;
 * catches race-window clicks while `usePhaseProgress` is still
 * loading). Both surfaces must agree on the threshold and the
 * tooltip total so a copy-edit on one side can't silently desync
 * from the other.
 *
 * Before this module existed the constants were duplicated as
 * `const` literals in each file and pinned by independent test
 * literals — a renderer-side change that drifted the threshold would
 * have passed both test suites. Importing from here forces a single
 * source.
 *
 * No runtime dependencies. Safe to import from either bundle (main
 * or renderer); contains only primitive constants.
 */
/**
 * Lowest build-progress phase at which `dev:start` is allowed to
 * proceed. Phases 1-3 produce no runnable workspace (planning, plan
 * review, framework only); Phase 4 is "Implementation: features (with
 * tests)" — the first phase that actually emits a game.
 */
export const PREVIEW_PHASE_GATE_MIN = 4;
/**
 * Total number of build-progress phases. Used in tooltip copy
 * (`current/total`) so the user sees how far they are from the gate.
 */
export const PREVIEW_PHASE_TOTAL = 7;
/**
 * CrucibleError code surfaced when the IPC preflight refuses a
 * `dev:start` call because the workspace is below
 * `PREVIEW_PHASE_GATE_MIN`. Distinct from the read/parse error codes
 * so the renderer can show "wait for the agent to advance the build"
 * rather than "fix the JSON".
 */
export const PREVIEW_PHASE_GATE_ERROR_CODE = 'CRUCIBLE-FOUNDRY-PREVIEW-PHASE-GATE';
/**
 * CrucibleError code surfaced when `docs/build-progress.json` exists
 * but cannot be parsed as a valid `BuildProgressFile`. The user is
 * told to fix the JSON; not retried automatically.
 */
export const PREVIEW_PROGRESS_PARSE_ERROR_CODE = 'CRUCIBLE-FOUNDRY-PREVIEW-PROGRESS-PARSE-ERROR';
/**
 * CrucibleError code surfaced when `docs/build-progress.json` exists
 * but cannot be read (EACCES / EPERM / EBUSY etc.). Conservative
 * refusal — we'd rather over-refuse than launch against a
 * misconfigured workspace.
 */
export const PREVIEW_PROGRESS_READ_ERROR_CODE = 'CRUCIBLE-FOUNDRY-PREVIEW-PROGRESS-READ-ERROR';
/**
 * CrucibleError code surfaced when `dev:start` refuses because the
 * game's dependencies are still being installed in the background
 * (issue #2584). After #2561 Part B the user is dropped into the
 * workspace early — Preview visible — while clone / install / lockfile-
 * regen finish in the background. A Preview launch during that window
 * used to trip the game's own auto-install against a not-yet-finished
 * tree and surface the alarming `CRUCIBLE-FOUNDRY-PNPM-INSTALL-FAILED`
 * card. This code is the FRIENDLY, retryable "still setting up" state
 * instead: distinct so the renderer can show the calm "try again in a
 * moment" copy rather than the install-FAILURE card. Signalled by the
 * install-state `installing` sentinel (create writes it on `seed-ready`
 * and clears it to `ok` once the install step finishes).
 */
export const PREVIEW_DEPS_INSTALLING_ERROR_CODE = 'CRUCIBLE-FOUNDRY-PREVIEW-DEPS-INSTALLING';
/**
 * Plain-language headline shown while the background create-install is
 * still running. Single source of truth so the main-process refusal
 * and any future renderer surface emit byte-identical copy. Plain
 * everyday words per the consumer-surface copy rule — no "pnpm",
 * "install", "lockfile", or "node_modules" jargon.
 */
export const PREVIEW_DEPS_INSTALLING_MESSAGE = 'Foundry is still setting up your game.';
/** Plain-language recovery line paired with the message above. */
export const PREVIEW_DEPS_INSTALLING_RECOVERY = 'This only takes a moment. Try Preview again in a few seconds.';
/**
 * Compose the user-facing tooltip / message shown when the phase
 * gate refuses a launch. Single source of truth — both the renderer
 * tooltip and the main-process refusal message call this helper so
 * a copy-edit lands in exactly one place.
 */
export function formatPreviewPhaseGateMessage(currentPhase, phaseLabel) {
    return `Game not yet runnable — current phase: ${currentPhase}/${PREVIEW_PHASE_TOTAL} — ${phaseLabel}`;
}
//# sourceMappingURL=preview-gate-constants.js.map